import { useCallback, useMemo } from "react";
import Graph_QL, { DynamicDataObk, StateDataObject } from "..";
import { useApolloClient } from "@apollo/client";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { gqlActions } from "../gqlData_Reducer";
import { BasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { filterByConnection } from "../filterByConnection";
import { AUTO_REPORT_CALLS, AutoReport, AutoReportResponse, CREATE_AUTO_REPORT, CREATE_AUTO_REPORT_AND_LINK, CreateAutoReportAndLinkResponse, CreateAutoReportResponse, DELETE_AUTO_REPORT, DeleteAutoReportResponse } from "./GQLCalls/autoReportCall";
import { AutoReportItem } from "./WidgetSlices/autoReportSlice";

export const useAutoReport = (connection: Graph_QL.GQLCons) => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();

  // #region | Auto Report State
  const autoReportState = useAppSelector(({graphQlData:{domain}}) => domain.autoReport);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(autoReportState, Graph_QL.ToArr(connection)) as Record<string, AutoReportItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<AutoReportItem>)
  }, [connection, autoReportState]);
  // #endregion

  // #region | Get Auto Report Info
  const getAutoReportInfo = useCallback(async (input: BasicQueryInfoInput, specific?: Graph_QL.GQLCons) => {
    const specificConnection = specific ? specific : connection;
    try {
      client.query<AutoReportResponse>({ query: AUTO_REPORT_CALLS, variables: { ...specificConnection.connection, input: { ids: input?.ids, offset: input?.offset, fetchAmount: input?.fetchAmount } } })
        .then((e) => {
          if (e && e.data) {
            dispatch({type: String(gqlActions.domain.autoReport.maintained), payload: {con: specificConnection.connection, data: e.data.autoReport.info}});
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
        console.log(error);
      }
  }, [client, connection, dispatch])
  // #endregion

  // #region | Maintain Auto Report Entries
  // const maintainEntries = async (autoReportData?: Partial<DynamicDataObk<AutoReport>>) => {
  //   const updatedAutoReport = autoReportData?.data;
  //   let newAutoReport = updatedAutoReport;
  //   if (newAutoReport && "__typename" in newAutoReport) {
  //     const { __typename, ...remaining } = newAutoReport;
  //     newAutoReport = remaining;
  //   }
  //   try {
  //     if (newAutoReport) {
  //       client.mutate<UpdateAutoReportResponse>({ mutation: UPDATE_AUTO_REPORT, variables: { ...connection.connection, input: [newAutoReport] } })
  //         .then((response) => {
  //         dispatch({type: String(gqlActions.domain.autoReport), payload: {con: connection.connection, data: response.data?.autoReport.update.updatedValues}})
  //       })
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }
  // #endregion

  // #region | Create Auto Report
  const createAutoReport = useCallback(async (autoReportData?: Partial<DynamicDataObk<Omit<AutoReport, "auto_report_id">>>): Promise<number | undefined> => {
    const createAutoReport = autoReportData?.data;
    let newAutoReport = createAutoReport;
    if (newAutoReport && "__typename" in newAutoReport) {
      const { __typename, ...remaining } = newAutoReport;
      newAutoReport = remaining;
    }
    try {
      if (newAutoReport) {
        return client.mutate<CreateAutoReportResponse>({ mutation: CREATE_AUTO_REPORT, variables: { ...connection.connection, input: [newAutoReport] }})
          .then((response) => {
            if (response && response.data && response.data.autoReport.create.success) {
              dispatch({ type: String(gqlActions.domain.autoReport.maintained), payload: { con: connection.connection, data: response.data.autoReport.create.updatedValues } });
              return response.data.autoReport.create.updatedValues[0].auto_report_id;
            }

          }).catch((error) => { console.log(error); return undefined; })
      }
    } catch (error) {
        console.log(error);
      }
    }, [client, connection.connection, dispatch])

  const createAutoReportAndLink = useCallback(async (autoReportData?: Partial<DynamicDataObk<Omit<AutoReport, "auto_report_id">>>, widgetId?: number) => {
    const createAutoReport = autoReportData?.data;
    let newAutoReport = createAutoReport;
    if (newAutoReport && "__typename" in newAutoReport) {
      const { __typename, ...remaining } = newAutoReport;
      newAutoReport = remaining;
    }
    try {
      if (newAutoReport && widgetId) {
        client.mutate<CreateAutoReportAndLinkResponse>({ mutation: CREATE_AUTO_REPORT_AND_LINK, variables: { ...connection.connection, input: [newAutoReport], widgetId} })
          .then((response) => {
            if (response && response.data && response.data.autoReport.createAndLinkWithWidget.success) {
              dispatch({ type: String(gqlActions.domain.autoReport.maintained), payload: { con: connection.connection, data: response.data.autoReport.createAndLinkWithWidget.updatedValues.newAutoReports } });
              dispatch({ type: String(gqlActions.domain.widgets.update), payload: {con: connection.connection, data: response.data.autoReport.createAndLinkWithWidget.updatedValues.updatedWidget}})
            }

          }).catch((error) => { console.log(error) })
      }
    } catch (error) {
        console.log(error);
      }
    }, [client, connection.connection, dispatch])
  // #endregion

  // #region | Delete AutoReport
  const deleteAutoReport = useCallback(async (autoReportData?: Partial<DynamicDataObk<AutoReport>>) => {
    const toDelete = autoReportData?.data;
    try {
      if (toDelete)
      client.mutate<DeleteAutoReportResponse>({mutation: DELETE_AUTO_REPORT, variables: {...connection.connection, ids: [toDelete.auto_report_id]}})
        .then((response) => {
          if (response.data?.autoReport.delete.success) {
            dispatch({
              type: String(gqlActions.domain.autoReport.deleted), payload: { con: connection.connection, data: [toDelete] }
            });
          } else {
            console.warn("Delete failed:", response.data?.autoReport.delete.message);
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
      console.log(error);
    }
  }, [client, connection.connection, dispatch])
  // #endregion

  return {call: getAutoReportInfo, createAutoReport, createAutoReportAndLink, deleteAutoReport, data: fullState };
}

